import type { FileRecord } from "@cohesive/api-client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { prepareSrcDocHtml } from "../../../lib/htmlIframeSrcDoc";
import { cn } from "../../../lib/utils";
import { getFileNameFromPath } from "../FileRecordCard/FileRecordCard";
import { HtmlBlockShield } from "./HtmlBlockShield";
import { HtmlCardPlaceholder } from "./HtmlCardPlaceholder";
import { useHtmlCardSrcDoc } from "./useHtmlCardSrcDoc";
import {
  useIframeMountAdmission,
  type IframeMountPolicy,
} from "./useIframeMountAdmission";

// Match the exact same zoom constants as ArtifactCardCanvas.
const REFERENCE_WIDTH = 350;
const BASE_SCALE = 0.35;
const EFFECTIVE_VIEWPORT_WIDTH = REFERENCE_WIDTH / BASE_SCALE; // ≈1000px

/** Module-level so the pipeline hook's delivery effect sees a stable identity
 *  — the thumb card has no render options to vary. */
const PREPARE_THUMB = (html: string) => prepareSrcDocHtml(html);

export type HtmlFileRecordThumbCardProps = Pick<
  FileRecord,
  "url" | "path" | "updated_at"
> & {
  className?: string;
  style?: React.CSSProperties;
  selected?: boolean;
  /** True while the parent NodeResizer is active — suppresses the hover
   *  shadow transition so resizing doesn't animate. */
  resizing?: boolean;
  /** Async rewrite applied before display — swaps embedded public file URLs
   *  for presigned ones (the sandbox sends no credentials). */
  transformHtml?: (html: string) => Promise<string> | string;
  /** `"progressive"` paces the iframe mount through the shared admission
   *  scheduler (public share view); default `"eager"` is the unchanged
   *  editor behavior. */
  mountPolicy?: IframeMountPolicy;
  /** Server-rendered card preview shown until the live iframe is up. */
  previewImageUrl?: string | null;
  onClick?: () => void;
  onDoubleClick?: () => void;
};

/**
 * Legacy scaled-tile preview for HTML file record canvas nodes: a ~1000px
 * virtual viewport scaled into the node's square tile, forced light,
 * display-only. This is what every HTML file record on canvas looked like
 * before HTML blocks, and it stays the renderer for any node whose data
 * carries no `presentation` marker, so existing canvases are unchanged.
 *
 * Scale mirrors ArtifactCardCanvas: ResizeObserver → contentScale →
 * `transform: scale()` anchored at origin-top-left.
 *
 * The iframe never takes pointer events; the shield above it is what keeps
 * canvas panning alive over the card (see HtmlBlockShield).
 */
export function HtmlFileRecordThumbCard({
  url,
  path,
  updated_at,
  className,
  style,
  selected,
  resizing = false,
  transformHtml,
  mountPolicy = "eager",
  previewImageUrl = null,
  onClick,
  onDoubleClick,
}: HtmlFileRecordThumbCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const [cardWidth, setCardWidth] = useState<number>(() => {
    return typeof style?.width === "number" && style.width > 0
      ? style.width
      : REFERENCE_WIDTH;
  });

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const w = entry.contentRect.width;
        if (w > 0) setCardWidth(w);
      });
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const contentScale = cardWidth / EFFECTIVE_VIEWPORT_WIDTH;

  const { displayDoc } = useHtmlCardSrcDoc({
    url,
    updated_at,
    transformHtml,
    optionsSalt: "",
    prepare: PREPARE_THUMB,
  });

  // `ready` keeps this card out of the scheduler until its document can
  // actually mount, so an admission slot always paces a real iframe.
  const { admitted, onIframeLoad } = useIframeMountAdmission({
    policy: mountPolicy,
    hostRef: cardRef,
    ready: displayDoc !== null,
  });

  const [isVisible, setIsVisible] = useState(false);
  const handleIframeLoad = useCallback(() => {
    setIsVisible(true);
    onIframeLoad();
  }, [onIframeLoad]);

  // Fade the new document in only when the frame actually remounts.
  const mountKey = displayDoc?.mountKey ?? null;
  useEffect(() => {
    if (mountKey) setIsVisible(false);
  }, [mountKey]);

  const displayTitle = (path && getFileNameFromPath(path)) || "HTML";

  return (
    <div
      ref={cardRef}
      className={cn(
        "group relative overflow-hidden rounded-xl",
        "aspect-square w-full",
        !resizing && "transition-shadow duration-200",
        "hover:shadow-2xl",
        selected && "ring-2 ring-canvas-selection/60",
        onClick && "cursor-pointer",
        className,
      )}
      style={{ containerType: "inline-size", ...style }}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      <div
        className="relative h-full w-full overflow-hidden bg-white"
        style={{ colorScheme: "light" }}
      >
        {/* Placeholder until the live document paints — the server-rendered
            preview when available (share view), else the file glyph. */}
        {!isVisible && (
          <HtmlCardPlaceholder previewImageUrl={previewImageUrl} showGlyph />
        )}

        <div className="relative overflow-hidden w-full h-full">
          <div
            className={cn(
              "absolute inset-0 z-0 origin-top-left transition-opacity duration-200 ease-out",
              isVisible ? "opacity-100" : "opacity-0",
            )}
            style={{
              transform: `scale(${contentScale})`,
              width: `${100 / contentScale}%`,
              height: `${100 / contentScale}%`,
            }}
          >
            {displayDoc && admitted && (
              <iframe
                key={displayDoc.mountKey}
                srcDoc={displayDoc.srcDoc}
                className="w-full h-full border-0"
                style={{ pointerEvents: "none", colorScheme: "light" }}
                sandbox="allow-scripts"
                title={displayTitle}
                onLoad={handleIframeLoad}
              />
            )}
          </div>
        </div>
      </div>
      <HtmlBlockShield />
    </div>
  );
}
